import { NoteLabel } from '@seen/core/models/noteLabel'
import { visitNoteCategoryByToken } from '@seen/core/models/visitNoteCategory'

import { Button, type ButtonProps, cn } from '@seen/ui'
import { type ReactNode, type Ref, useMemo } from 'react'
import type { Field } from '#src/components/core/form/FormField.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { NoteLabelCombobox } from '#src/components/NoteLabelCombobox.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

type Base = Field<string[]> & Omit<ButtonProps, 'value' | 'onChange'>
export interface NoteLabelComboboxButtonProps extends Base {
  ref?: Ref<HTMLButtonElement>
  /** Also offer the hard-coded visit-note categories as pick-only chips. */
  includeCategories?: boolean
}

interface ResolvedChip {
  value: string
  color: string | undefined
  name: string
}

export function NoteLabelComboboxButton({
  value: valueMaybeIncludingDeleted,
  onChange,
  className,
  ref,
  includeCategories = false,
  ...rest
}: NoteLabelComboboxButtonProps): ReactNode {
  const { data: labels } = useSearch(NoteLabel, NoteLabel.baseSearchQuery())

  // Resolve each selected value to a chip from either the hard-coded category
  // registry or the user-label set. Deleted labels (no match in either) drop
  // out, matching the prior label-only behavior.
  const chips = useMemo<ResolvedChip[]>(
    () =>
      valueMaybeIncludingDeleted.flatMap((v) => {
        const category = includeCategories
          ? visitNoteCategoryByToken(v)
          : undefined
        if (category) {
          return [{ value: v, color: category.color, name: category.display }]
        }
        const label = labels?.find((l) => l.reference.reference === v)
        if (label) {
          return [{ value: v, color: label.color, name: label.name }]
        }
        return []
      }),
    [valueMaybeIncludingDeleted, labels, includeCategories]
  )

  return (
    <NoteLabelCombobox
      value={valueMaybeIncludingDeleted}
      onChange={onChange}
      includeCategories={includeCategories}
    >
      <Button
        variant="outline"
        className={cn('flex items-center gap-2 shadow-sm', className)}
        size={chips.length ? undefined : 'icon'}
        ref={ref}
        {...rest}
      >
        {!chips.length && <IconLabel />}
        {chips.length === 1 && (
          <div className="flex items-center gap-1">
            <LabelColor color={chips[0].color} />
            {chips[0].name}
          </div>
        )}
        {chips.length > 1 && (
          <div className="flex items-center gap-1">
            <div className="space-x-[-0.28125rem] flex rtl:space-x-reverse">
              {chips.slice(0, 3).map((chip) => (
                <div
                  key={chip.value}
                  className="relative rounded-full border border-white bg-white"
                >
                  <LabelColor color={chip.color} />
                </div>
              ))}
            </div>
            {/* "selected" rather than "labels" once categories can mix in, so the
             * count doesn't mislabel category chips; label-only callers keep "labels". */}
            {chips.length} {includeCategories ? 'selected' : 'labels'}
          </div>
        )}
      </Button>
    </NoteLabelCombobox>
  )
}
